import React, { useState, useCallback, useEffect } from "react";
import Logo from "../assets/logo.png";
import { Box, Button, Container, FormControl, InputAdornment, MenuItem, Paper, Select, TextField, Typography, makeStyles, useMediaQuery, useTheme, Snackbar } from "@material-ui/core";
import { AccountCircle } from "@material-ui/icons";
import MuiAlert from "@material-ui/lab/Alert";
import { baseMobileUrl, baseUrl } from "../auth/secret";
import { Helmet } from "react-helmet";
import variables from "../constants/Variables.scss";

const API_URL = [baseMobileUrl, baseUrl];

// Alert component for notifications
const Alert = React.memo((props) => {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
});

const headersJson = () => {
  return {
    Accept: "*/*",
    "Content-Type": "application/json",
  };
};

const submitUserDeleteRequest = async (payload) => {
  try {
    const sanitizedPayload = {
      username: (payload.username || "").trim(),
      reasonId: payload.reasonId,
    };

    if (!sanitizedPayload.username || !sanitizedPayload.reasonId) {
      throw new Error("Invalid request data");
    }

    let lastError;
    for (const url of API_URL) {
      try {
        const response = await fetch(`${url}/user/user-mobile-delete-request`, {
          method: "POST",
          headers: headersJson(),
          body: JSON.stringify(sanitizedPayload),
        });

        if (response.ok) {
          return await response.json();
        } else {
          const errorData = await response.json().catch(() => ({}));
          lastError = new Error(errorData.message || `Request failed with status ${response.status}`);
        }
      } catch (err) {
        lastError = err;
      }
    }

    throw lastError || new Error("All API requests failed");
  } catch (error) {
    console.error("API request failed:", error);
    throw error;
  }
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: "#EDF2F7",
    padding: "0 15px",
    [theme.breakpoints.down("xs")]: {
      padding: "0",
      backgroundColor: "#FFFFFF",
    },
  },
  paper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    maxWidth: "450px",
    minHeight: "600px",
    padding: theme.spacing(5, 3),
    borderRadius: "8px",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(3, 2),
      minHeight: "100vh",
      borderRadius: "0",
      boxShadow: "none",
      maxWidth: "100%",
    },
  },
  logo: {
    width: "30%",
    height: "auto",
    marginBottom: "25px",
    [theme.breakpoints.down("xs")]: {
      width: "50%",
      height: "auto",
      marginBottom: "20px",
    },
  },
  headerBox: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "30px",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      marginBottom: "20px",
    },
  },
  title: {
    fontWeight: "bold",
    textAlign: "center",
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.2rem",
    },
  },
  subtitle: {
    marginTop: "5px",
    textAlign: "center",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.6rem",
    },
  },
  formBox: {
    width: "100%",
    marginBottom: "20px",
  },
  reasonBox: {
    width: "100%",
    marginBottom: "30px",
    [theme.breakpoints.down("xs")]: {
      marginBottom: "20px",
    },
  },
  formLabel: {
    marginBottom: "8px",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.8rem",
    },
  },
  requiredMark: {
    color: "red",
  },
  inputField: {
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: "#8e6e64",
      },
    },
    "& .MuiFormLabel-root.Mui-focused": {
      color: "#8e6e64",
    },
  },
  selectField: {
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: "#8e6e64",
      },
    },
  },
  button: {
    color: "white",
    backgroundColor: variables.COLOR_PRIMARY,
    "&:focus": {
      outline: "none",
      border: "none",
    },
    "&:hover": {
      backgroundColor: variables.COLOR_PRIMARY,
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.875rem",
    },
  },
  footerText: {
    marginTop: "30px",
    color: "#000",
    textAlign: "center",
    [theme.breakpoints.down("xs")]: {
      marginTop: "20px",
      fontSize: "0.7rem",
    },
  },
}));

const DeleteUserRequest = () => {
  const [formState, setFormState] = useState({
    username: "",
    reason: "",
  });
  const [touched, setTouched] = useState({
    username: false,
    reason: false,
  });
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const classes = useStyles();

  // Define reasons list
  const reasons = [
    { value: 1, label: "No longer need the account" },
    { value: 2, label: "Switching to a different account" },
    { value: 3, label: "Privacy concerns" },
    { value: 4, label: "Dissatisfied with the service" },
    { value: 5, label: "Other personal reasons" },
  ];

  // Validate form fields
  const validateForm = useCallback(() => {
    const errors = {};
    if (!formState.username.trim()) {
      errors.username = "Username is required";
    }
    if (!formState.reason) {
      errors.reason = "Please select a reason";
    }
    return errors;
  }, [formState]);

  const errors = validateForm();
  const isValid = Object.keys(errors).length === 0;

  // Handle input changes
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  // Handle field blur for validation
  const handleBlur = useCallback((field) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
  }, []);

  // Show alert message with timeout
  const showAlert = useCallback((message, severity = "success") => {
    setAlert({
      open: true,
      message,
      severity,
    });
  }, []);

  // Close alert message
  const handleCloseAlert = useCallback((event, reason) => {
    if (reason === "clickaway") return;
    setAlert((prev) => ({ ...prev, open: false }));
  }, []);

  // Reset form to initial state
  const resetForm = useCallback(() => {
    setFormState({
      username: "",
      reason: "",
    });
    setTouched({
      username: false,
      reason: false,
    });
  }, []);

  // Submit form
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setTouched({
        username: true,
        reason: true,
      });
      if (!isValid) {
        showAlert("Please fill in all required fields", "error");
        return;
      }
      setIsSubmitting(true);
      try {
        const payload = {
          username: formState.username,
          reasonId: formState.reason,
        };
        const response = await submitUserDeleteRequest(payload);
        const status = response?.body?.status ? "success" : "warning";
        if (status === "success" || response?.header?.result) {
          showAlert("Request submitted successfully!");
          resetForm();
        } else {
          showAlert("Failed to submit request. Please try again.", "error");
        }
      } catch (error) {
        showAlert(error.message || "Failed to submit request. Please try again.", "error");
      } finally {
        setIsSubmitting(false);
      }
    },
    [formState, isValid, showAlert, resetForm],
  );

  // Auto-close alert after 5 seconds
  useEffect(() => {
    let timerId;
    if (alert.open) {
      timerId = setTimeout(() => {
        setAlert((prev) => ({ ...prev, open: false }));
      }, 5000);
    }
    return () => clearTimeout(timerId);
  }, [alert.open]);

  // Set viewport meta tag when component mounts and restore on unmount
  useEffect(() => {
    // Save the original viewport meta tag
    const originalViewport = document.querySelector('meta[name="viewport"]');
    const originalContent = originalViewport ? originalViewport.getAttribute("content") : "";

    // Create or update viewport meta tag for this route
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) {
      viewportMeta = document.createElement("meta");
      viewportMeta.setAttribute("name", "viewport");
      document.head.appendChild(viewportMeta);
    }

    // Set the specific viewport content for this route
    viewportMeta.setAttribute("content", "width=device-width, initial-scale=1");

    // Cleanup function to restore original viewport on unmount
    return () => {
      if (originalViewport) {
        originalViewport.setAttribute("content", originalContent || "width=1250, initial-scale=1");
      } else {
        // Cleanup if we created a new meta tag
        const metaToRemove = document.querySelector('meta[name="viewport"]');
        if (metaToRemove) {
          document.head.removeChild(metaToRemove);
        }
      }
    };
  }, []);

  return (
    <>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>

      <Container maxWidth={false} className={classes.container} disableGutters={isMobile}>
        <Paper className={classes.paper} elevation={isMobile ? 0 : 3}>
          <img src={Logo} alt="logo" className={classes.logo} />

          <Box className={classes.headerBox}>
            <Typography variant="h5" className={classes.title}>
              Request to delete account
            </Typography>
            <Typography variant="overline" className={`ut-color-primary ${classes.subtitle}`}>
              Please input username and reason
            </Typography>
          </Box>

          <form onSubmit={handleSubmit} style={{ width: "100%" }} noValidate>
            <Box className={classes.formBox}>
              <Typography variant="body2" className={classes.formLabel}>
                Username<span className={classes.requiredMark}>*</span>
              </Typography>
              <TextField
                autoComplete="off"
                name="username"
                placeholder="Enter Username"
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                fullWidth
                value={formState.username}
                onChange={handleChange}
                onBlur={() => handleBlur("username")}
                error={touched.username && Boolean(errors.username)}
                helperText={touched.username && errors.username}
                className={classes.inputField}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <AccountCircle className="ut-color-primary" />
                    </InputAdornment>
                  ),
                }}
                disabled={isSubmitting}
                inputProps={{ maxLength: 50 }}
              />
            </Box>

            <Box className={classes.reasonBox}>
              <Typography variant="body2" className={classes.formLabel}>
                Reason<span className={classes.requiredMark}>*</span>
              </Typography>
              <FormControl variant="outlined" size={isMobile ? "small" : "medium"} fullWidth className={classes.selectField} error={touched.reason && Boolean(errors.reason)} disabled={isSubmitting}>
                <Select name="reason" value={formState.reason} onChange={handleChange} onBlur={() => handleBlur("reason")} displayEmpty>
                  <MenuItem value="" disabled>
                    Select Reason
                  </MenuItem>
                  {reasons.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
                {touched.reason && errors.reason && (
                  <Typography variant="caption" color="error">
                    {errors.reason}
                  </Typography>
                )}
              </FormControl>
            </Box>

            <Button type="submit" size={isMobile ? "medium" : "large"} variant="contained" fullWidth className={classes.button} disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Request"}
            </Button>
          </form>

          <Typography variant="caption" className={classes.footerText}>
            Powered by <span className="ut-color-primary">UDAYA TECHNOLOGY Co., Ltd.</span>
          </Typography>
        </Paper>

        <Snackbar open={alert.open} autoHideDuration={5000} onClose={handleCloseAlert} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
          <Alert onClose={handleCloseAlert} severity={alert.severity}>
            {alert.message}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default DeleteUserRequest;
